import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Clock } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GREEN = "#4E7A56";
const BLUE = "#2C5F7C";
const CREAM = "#F6F5EE";
const NAME = "Family Tree Dental Care";
const FB = "https://www.facebook.com/FamilyTreeDentalDgte/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function FamilyTreeConcept() {
  const [open, setOpen] = useState(false);
  const links = [["Our Clinic", "#about"], ["Care", "#care"], ["Hours", "#hours"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#232A22", fontFamily: "'Lexend', sans-serif" }} data-testid="familytree-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&family=Lexend:wght@300;400;500;600&display=swap'); .ft-serif{font-family:'Fraunces',serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ft-logo" className="flex items-center gap-3">
            <img src="/dental/family-tree-dental-concept/logo.jpg" alt={NAME} className="h-11 w-11 object-contain" />
            <span className="ft-serif text-xl" style={{ color: GREEN }}>Family Tree Dental Care</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#3A4438] hover:text-[#4E7A56] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="ft-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: BLUE }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="ft-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — the clinic's real blue-toned reception, tree logo lit up */}
      <section className="relative min-h-[86vh] flex items-center" data-testid="ft-hero">
        <img src="/dental/family-tree-dental-concept/interior.jpg" alt="Family Tree Dental Care reception" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(15,22,34,0.96) 15%, rgba(15,22,34,0.78) 55%, rgba(15,22,34,0.45) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: "#9ED3A8" }}>General Dentistry &amp; Orthodontics · Dumaguete City</span>
            <h1 className="mt-5 text-5xl sm:text-6xl ft-serif text-white leading-[1.08]">Trusted, Affordable Care for the Whole Family</h1>
            <p className="mt-5 text-lg text-white/75 max-w-lg">Dr. Trisha Angelique T. Sagun and Dr. Ronald C. Sagun welcome patients of every age at Sta. Catalina St.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#visit" data-testid="ft-hero-book" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: BLUE }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ft-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-7 text-sm text-white/60">4.2★ Rating · 22 Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl ft-serif leading-relaxed" style={{ color: GREEN }}>
          Family Tree Dental Care provides general and orthodontic dentistry for the whole family, from Sta. Catalina Street in Dumaguete City.
        </motion.p>
      </section>

      {/* SIGNAGE */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <motion.img {...fadeUp} src="/dental/family-tree-dental-concept/signage.jpg" alt="Family Tree Dental Care signage — Dr. Trisha Angelique T. Sagun & Dr. Ronald C. Sagun" className="w-full object-cover shadow-lg" />
      </section>

      {/* CARE */}
      <section id="care" className="py-20" style={{ background: "#EAF0E6" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10">
          <motion.div {...fadeUp} className="overflow-hidden shadow-md">
            <img src="/dental/family-tree-dental-concept/team.jpg" alt="The Family Tree Dental Care team at work" className="w-full h-72 object-cover" />
            <p className="p-5 bg-white font-medium">Our team, at work chairside</p>
          </motion.div>
          <motion.div {...fadeUp} className="overflow-hidden shadow-md">
            <img src="/dental/family-tree-dental-concept/braces.jpg" alt="Braces care at Family Tree Dental" className="w-full h-72 object-cover" />
            <p className="p-5 bg-white font-medium">Orthodontic care, before and after</p>
          </motion.div>
        </div>
      </section>

      {/* HOURS */}
      <section id="hours" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.div {...fadeUp} className="inline-flex items-center gap-3 px-8 py-5" style={{ background: GREEN, color: "white" }}>
          <Clock size={22} />
          <div className="text-left">
            <p className="font-semibold">Monday – Saturday, 8am – 8pm</p>
            <p className="text-sm text-white/80">Sundays &amp; holidays by appointment</p>
          </div>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="ft-serif text-3xl sm:text-4xl text-center mb-14" style={{ color: GREEN }}>What Patients Say</motion.h2>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { text: "The doctor is informative and nice, and the clinic is impressively clean.", author: "Reasonable-Neat8738", source: "Reddit r/dumaguete" },
            { text: "Had an impacted wisdom tooth removed — the clinic was very clean and aftercare was clearly explained.", author: "Aiaz15", source: "Reddit r/dumaguete" },
            { text: "Recommend Family Tree for orthodontic questions too — they're gentle and explain procedures thoroughly.", author: "MoraM5358", source: "Reddit r/dumaguete" },
            { text: "The ambiance makes dental visits feel less fearful — patients now actually look forward to check-ups.", author: "Facebook group / local visitor post" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`ft-testimonial-${i}`} className="bg-white p-7 shadow-sm">
              <p className="text-[#2A3428]/85 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 text-sm font-semibold" style={{ color: GREEN }}>{q.author}{q.source ? ` · ${q.source}` : ""}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-20" style={{ background: BLUE }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="ft-serif text-3xl text-white">Visit Us</h2>
            <ul className="mt-7 space-y-5 text-white/80">
              <li className="flex items-start gap-4"><MapPin size={20} className="mt-0.5 text-white" />G/F Leq Tak Building, Sta. Catalina St., Dumaguete City 6200</li>
              <li className="flex items-start gap-4"><Phone size={20} className="mt-0.5 text-white" /><a href="tel:+639062343950" className="hover:underline">0906 234 3950</a> · (035) 422-1577</li>
              <li className="flex items-start gap-4"><Facebook size={20} className="mt-0.5 text-white" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ft-facebook-link" className="hover:underline">FamilyTreeDentalDgte</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px] shadow-md">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Sta Catalina St Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A6255]">
          <p className="ft-serif text-base" style={{ color: GREEN }}>Family Tree Dental Care</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: BLUE }}>Message Family Tree</a>
      <ConceptCTAPill slug="family-tree-dental-concept" />
    </div>
  );
}
